import {
  type GravidadeDeAlerta,
  type IntencaoDeStatus,
  type NivelDeBlindagem,
  type OrigemDoCusto,
  type StatusDoVeiculo,
  ROTULO_DO_NIVEL_DE_BLINDAGEM,
  ROTULO_DO_STATUS_DE_VEICULO,
} from '@locaveri/shared'
import {
  Banknote,
  Calculator,
  CalendarClock,
  Car,
  CircleCheck,
  CircleSlash,
  CircleX,
  Clock,
  FileText,
  Hammer,
  type LucideIcon,
  ShieldCheck,
  Sparkles,
  Tag,
  TriangleAlert,
  Wrench,
} from 'lucide-react'
import type { ReactElement } from 'react'

import { Badge } from '@/components/ui/badge'

/**
 * Selos de frota.
 *
 * ---------------------------------------------------------------------------
 * COR NUNCA COMUNICA SOZINHA
 * ---------------------------------------------------------------------------
 * Regra do CLAUDE.md, e aqui ela não é estética: quem não distingue verde de
 * âmbar precisa saber se o carro PODE SAIR. Todo selo daqui tem cor + ÍCONE +
 * RÓTULO, e o rótulo vem de `@locaveri/shared` — a mesma string que a API e o
 * app usam.
 *
 * ---------------------------------------------------------------------------
 * AMARELO É MARCA, NÃO AVISO
 * ---------------------------------------------------------------------------
 * Nenhum selo desta tela usa a variante `brand` (o amarelo do logo). Aviso é
 * `warning`, que é âmbar e visualmente distinto da marca; se o amarelo
 * aparecesse em "manutenção preventiva", a tabela da frota viraria um campo de
 * amarelos onde nada mais chama atenção — e o CTA da tela perderia o único
 * lugar em que o olho deveria parar.
 */

/**
 * A intenção do domínio vira variante visual aqui, e não no `@locaveri/shared`:
 * o pacote descreve o SIGNIFICADO do status (`critica`, `atencao`), a tela
 * escolhe a cor. `SINISTRO` é a única crítica dos onze status e `DISPONIVEL` é
 * a única positiva — é exatamente o contraste que a tabela precisa ter.
 */
const VARIANTE_DA_INTENCAO = {
  positiva: 'success',
  informativa: 'info',
  neutra: 'neutral',
  atencao: 'warning',
  critica: 'danger',
} as const satisfies Readonly<Record<IntencaoDeStatus, string>>

/**
 * O ícone de cada status. Preventiva e corretiva compartilham a chave inglesa
 * de propósito — o que as separa é o rótulo, e inventar dois ícones parecidos
 * só faria a coluna ficar ilegível de longe.
 */
const ICONE_DO_STATUS: Readonly<Record<StatusDoVeiculo, LucideIcon>> = {
  DISPONIVEL: CircleCheck,
  RESERVADO: CalendarClock,
  EM_LOCACAO: Car,
  LIMPEZA: Sparkles,
  MANUTENCAO_PREVENTIVA: Wrench,
  MANUTENCAO_CORRETIVA: Wrench,
  FUNILARIA: Hammer,
  SINISTRO: TriangleAlert,
  DOCUMENTACAO: FileText,
  INDISPONIVEL: CircleSlash,
  EM_VENDA: Tag,
}

export function BadgeDeStatusDoVeiculo({
  status,
}: {
  readonly status: StatusDoVeiculo
}): ReactElement {
  const { rotulo, intencao } = ROTULO_DO_STATUS_DE_VEICULO[status]
  const Icone = ICONE_DO_STATUS[status]

  return (
    <Badge variant={VARIANTE_DA_INTENCAO[intencao]}>
      <Icone aria-hidden="true" />
      {rotulo}
    </Badge>
  )
}

/**
 * O nível de proteção balística (NIJ 0108).
 *
 * `outline` e não `brand`: a blindagem é o atributo central do produto e
 * aparece em TODA linha da frota e do catálogo — pintá-la de amarelo deixaria a
 * tela inteira amarela, que é precisamente o que o CLAUDE.md proíbe. O peso
 * visual aqui vem do ícone de escudo, não da cor.
 */
export function BadgeDeNivelDeBlindagem({
  nivel,
}: {
  readonly nivel: NivelDeBlindagem
}): ReactElement {
  return (
    <Badge variant="outline" title="Proteção balística pela NIJ 0108">
      <ShieldCheck aria-hidden="true" />
      {ROTULO_DO_NIVEL_DE_BLINDAGEM[nivel]}
    </Badge>
  )
}

/**
 * Caixa × competência — o selo que impede alguém de somar as duas colunas.
 *
 * `LANCAMENTO_MANUAL` é o que SAIU DO BANCO no mês; `CALCULO_AUTOMATICO` é o
 * que o mês CONSUMIU (depreciação, capital, overhead e os duodécimos do IPVA).
 * O mesmo pagamento aparece nos dois, em meses diferentes — somar conta o
 * dinheiro duas vezes e dobra o custo da frota no relatório.
 *
 * Por isso o rótulo diz "Caixa" e "Competência" em vez de repetir o nome do
 * enum: `LANCAMENTO_MANUAL` descreve COMO a linha nasceu, e o que a pessoa
 * precisa saber é O QUE ela significa no relatório.
 */
const ORIGEM = {
  LANCAMENTO_MANUAL: {
    rotulo: 'Caixa',
    variante: 'neutral',
    icone: Banknote,
    explicacao: 'Saiu do caixa neste mês. Não some com a coluna de competência.',
  },
  CALCULO_AUTOMATICO: {
    rotulo: 'Competência',
    variante: 'info',
    icone: Calculator,
    explicacao: 'O que o mês consumiu, calculado pelo fechamento. Não some com a coluna de caixa.',
  },
} as const satisfies Readonly<Record<OrigemDoCusto, unknown>>

export function BadgeDeOrigemDeCusto({ origem }: { readonly origem: OrigemDoCusto }): ReactElement {
  const { rotulo, variante, icone: Icone, explicacao } = ORIGEM[origem]

  return (
    <Badge variant={variante} title={explicacao}>
      <Icone aria-hidden="true" />
      {rotulo}
    </Badge>
  )
}

/**
 * A gravidade de um vencimento.
 *
 * `VENCIDO` é vermelho e separado de `CRITICO` porque a AÇÃO é outra: um
 * certificado a vencer se renova; um vencido tira o carro de circulação. Os
 * três ícones são diferentes de propósito — em impressão preto e branco (o
 * relatório de frota vai para o papel) a cor não sobrevive, o ícone sim.
 */
const GRAVIDADE = {
  VENCIDO: { rotulo: 'Vencido', variante: 'danger', icone: CircleX },
  CRITICO: { rotulo: 'Crítico', variante: 'warning', icone: TriangleAlert },
  ATENCAO: { rotulo: 'Atenção', variante: 'info', icone: Clock },
} as const satisfies Readonly<Record<GravidadeDeAlerta, unknown>>

export function BadgeDeGravidadeDeAlerta({
  gravidade,
}: {
  readonly gravidade: GravidadeDeAlerta
}): ReactElement {
  const { rotulo, variante, icone: Icone } = GRAVIDADE[gravidade]

  return (
    <Badge variant={variante}>
      <Icone aria-hidden="true" />
      {rotulo}
    </Badge>
  )
}
